import {
  ACCESS_KEYS,
  type AccessKey,
  type PermissionLayout,
  type ResolvedLayout,
  type Scope,
} from './types';

/**
 * Единственное место, где описано, что открывает разрешение.
 *
 * Слаги совпадают со справочником permissions на бэкенде. Права складываются:
 * человек видит объединение того, что дают все его разрешения.
 *
 * Набор разделов методолога согласован с куратором. Набор для user — пока
 * предложение, его нужно подтвердить до того, как бэкенд начнёт отдавать права.
 */
export const PERMISSION_LAYOUTS: Record<string, PermissionLayout> = {
  admin: {
    slug: 'admin',
    labelId: 'permissions.admin',
    menu: '*',
    welcomeFrames: '*',
    grants: '*',
  },
  developer: {
    slug: 'developer',
    labelId: 'permissions.developer',
    menu: '*',
    welcomeFrames: '*',
    grants: '*',
  },
  methodologist: {
    slug: 'methodologist',
    labelId: 'permissions.methodologist',
    menu: ['/welcome', '/monitoring', '/communications', '/communication-editor', '/school'],
    welcomeFrames: ['access', 'about', 'chat-stats', 'recent-users'],
    grants: [],
  },
  user: {
    slug: 'user',
    labelId: 'permissions.user',
    menu: ['/welcome', '/monitoring'],
    welcomeFrames: ['access', 'about', 'chat-stats'],
    grants: [],
  },
};

/** Раскладка, когда про права ничего не известно: не прячем ничего. */
export const FULL_ACCESS: ResolvedLayout = {
  menu: '*',
  welcomeFrames: '*',
  grants: '*',
};

export function normalizePermissionSlug(raw: unknown): string {
  if (typeof raw === 'string') return raw.trim().toLowerCase();
  if (raw && typeof raw === 'object') {
    const candidate = raw as { slug?: unknown; name?: unknown };
    if (typeof candidate.slug === 'string') return candidate.slug.trim().toLowerCase();
    if (typeof candidate.name === 'string') return candidate.name.trim().toLowerCase();
  }
  return '';
}

/** Бэкенд может прислать массив строк или массив объектов справочника. */
export function normalizePermissionList(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const slugs = raw.map(normalizePermissionSlug).filter(Boolean);
  return Array.from(new Set(slugs));
}

export function listPermissionSlugs(): string[] {
  return Object.keys(PERMISSION_LAYOUTS);
}

export function getPermissionLayout(slug: string): PermissionLayout | undefined {
  return PERMISSION_LAYOUTS[normalizePermissionSlug(slug)];
}

function mergeScope<T extends string>(scopes: Scope<T>[]): Scope<T> {
  if (scopes.some((scope) => scope === '*')) return '*';
  const merged = new Set<T>();
  scopes.forEach((scope) => {
    if (scope !== '*') scope.forEach((value) => merged.add(value));
  });
  return Array.from(merged);
}

/**
 * Собрать раскладку по разрешениям пользователя.
 *
 * Разрешения, которых нет в реестре, при объединении игнорируются — они просто
 * ничего не добавляют. Но если ни одно разрешение не опознано, мы о правах
 * ничего не знаем и отдаём полный доступ, чтобы не спрятать разделы у людей,
 * которым права ещё не раздали.
 */
export function resolveLayout(permissions: string[]): ResolvedLayout {
  const known = permissions
    .map(getPermissionLayout)
    .filter((layout): layout is PermissionLayout => Boolean(layout));

  if (known.length === 0) return FULL_ACCESS;

  return {
    menu: mergeScope(known.map((layout) => layout.menu)),
    welcomeFrames: mergeScope(known.map((layout) => layout.welcomeFrames)),
    grants: mergeScope(known.map((layout) => layout.grants)),
  };
}

/**
 * Точечная проверка права. Пока список пуст, то есть бэкенд права не отдал,
 * возвращает true — ничего не прячем.
 */
export function hasPermission(permissions: string[], slug: string): boolean {
  if (permissions.length === 0) return true;
  return permissions.includes(normalizePermissionSlug(slug));
}

export function isMenuPathAllowed(layout: ResolvedLayout, path?: string): boolean {
  if (layout.menu === '*') return true;
  if (!path) return true;
  return layout.menu.some((allowed) => path === allowed || path.startsWith(`${allowed}/`));
}

const ALWAYS_ACCESSIBLE = ['/welcome', '/user/login'];

/**
 * Можно ли оставаться на этом URL после смены позиции.
 * Главная всегда доступна — туда уводим с чужих разделов.
 */
export function isAccessibleAppPath(layout: ResolvedLayout, path?: string): boolean {
  if (!path || path === '/') return true;
  if (ALWAYS_ACCESSIBLE.some((allowed) => path === allowed || path.startsWith(`${allowed}/`))) {
    return true;
  }
  if (path === '/chat' || path.startsWith('/chat/')) {
    return isMenuPathAllowed(layout, '/monitoring');
  }
  return isMenuPathAllowed(layout, path);
}

export function isWelcomeFrameAllowed(layout: ResolvedLayout, frameId: string): boolean {
  return layout.welcomeFrames === '*' || layout.welcomeFrames.includes(frameId);
}

/** Развернуть grants в объект, который ожидает Umi access. */
export function resolveGrants(layout: ResolvedLayout): Record<AccessKey, boolean> {
  const grants = layout.grants;
  return ACCESS_KEYS.reduce(
    (acc, key) => {
      acc[key] = grants === '*' || grants.includes(key);
      return acc;
    },
    {} as Record<AccessKey, boolean>,
  );
}
